import type { Tool, SchemaProperty } from '@/api/tools/tools.types';

// Fields to exclude from export (sensitive or auto-generated)
const EXCLUDED_FIELDS = [
  'id',
  '_id', 
  'orgId',
  'organization_id',
  'createdAt',
  'updatedAt',
  'created_at',
  'updated_at'
] as const;

// Configuration that should be exported
export interface ExportableToolConfig {
  type: Tool['type'];
  async?: boolean;
  function?: Tool['function'];
  server?: Omit<Tool['server'], 'secret'>; // Exclude secret for security
  messages?: Tool['messages'];
  destinations?: Tool['destinations'];
  knowledgeBases?: Tool['knowledgeBases'];
}

/**
 * Export tool configuration to a clean JSON object
 */
export const exportToolConfig = (tool: Tool): ExportableToolConfig => {
  // Create a clean copy without excluded fields
  const exportConfig: Record<string, unknown> = {};
  
  // Copy all allowed fields
  (Object.keys(tool) as Array<keyof Tool>).forEach(key => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!EXCLUDED_FIELDS.includes(key as any)) {
      exportConfig[key] = tool[key];
    }
  });

  // Handle server config specially to exclude secret
  if (tool.server) {
    const { secret, ...serverWithoutSecret } = tool.server;
    exportConfig.server = serverWithoutSecret;
  }

  // Handle knowledge bases specially to exclude provider field
  if (tool.knowledgeBases && Array.isArray(tool.knowledgeBases)) {
    exportConfig.knowledgeBases = tool.knowledgeBases.map(kb => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { provider, ...kbWithoutProvider } = kb as any;
      return kbWithoutProvider;
    });
  }

  // Ensure we have all required fields with proper typing
  return {
    type: tool.type,
    async: exportConfig.async as boolean | undefined,
    function: exportConfig.function as Tool['function'],
    server: exportConfig.server as Omit<Tool['server'], 'secret'>,
    messages: exportConfig.messages as Tool['messages'],
    destinations: exportConfig.destinations as Tool['destinations'],
    knowledgeBases: exportConfig.knowledgeBases as Tool['knowledgeBases']
  };
};

/**
 * Download tool configuration as JSON file
 */
export const downloadToolConfig = (tool: Tool) => {
  const config = exportToolConfig(tool);
  
  // Create filename with tool name and timestamp
  const timestamp = new Date().toISOString().split('T')[0];
  const toolName = tool.function?.name || 'unnamed_tool';
  const sanitizedName = toolName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  const filename = `tool_${sanitizedName}_${timestamp}.json`;
  
  // Create and download file
  const blob = new Blob([JSON.stringify(config, null, 2)], { 
    type: 'application/json' 
  });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  return filename;
};

/**
 * Validate imported configuration structure
 */
export const validateImportedConfig = (config: unknown): { 
  isValid: boolean; 
  errors: string[];
  warnings: string[];
} => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if it's a valid object
  if (!config || typeof config !== 'object') {
    errors.push('Invalid configuration format. Expected a JSON object.');
    return { isValid: false, errors, warnings };
  }

  // Type guard to ensure config is an object
  const configObj = config as Record<string, unknown>;

  // Required fields validation
  if (!configObj.type || typeof configObj.type !== 'string') {
    errors.push('Tool type is required and must be a string.');
  } else {
    // Validate type is one of the allowed types
    const validTypes = ['function', 'dtmf', 'endCall', 'transferCall', 'output', 'mcp', 'query', 'voicemail', 'handoff'];
    if (!validTypes.includes(configObj.type as string)) {
      errors.push(`Invalid tool type: ${configObj.type}. Must be one of: ${validTypes.join(', ')}`);
    }
  }

  // Function validation (if present)
  if (configObj.function) {
    if (typeof configObj.function !== 'object') {
      errors.push('Function configuration must be an object.');
    } else {
      const func = configObj.function as Record<string, unknown>;
      if (!func.name || typeof func.name !== 'string') {
        errors.push('Function name is required and must be a string.');
      }
      if (func.description && typeof func.description !== 'string') {
        errors.push('Function description must be a string.');
      }
    }
  }

  // Server validation (if present)
  if (configObj.server && typeof configObj.server !== 'object') {
    errors.push('Server configuration must be an object.');
  }

  // Messages validation (if present)
  if (configObj.messages) {
    if (!Array.isArray(configObj.messages)) {
      errors.push('Messages must be an array.');
    }
  }

  // Knowledge bases validation (if present for query type)
  if (configObj.type === 'query' && configObj.knowledgeBases) {
    if (!Array.isArray(configObj.knowledgeBases)) {
      errors.push('Knowledge bases must be an array.');
    }
  }

  // Destinations validation (if present for transfer/handoff types)
  if ((configObj.type === 'transferCall' || configObj.type === 'handoff') && configObj.destinations) {
    if (!Array.isArray(configObj.destinations)) {
      errors.push('Destinations must be an array.');
    }
  }

  // Check for sensitive fields that shouldn't be in import
  const sensitiveFields = ['id', '_id', 'orgId', 'organization_id', 'createdAt', 'updatedAt'];
  sensitiveFields.forEach(field => {
    if (configObj[field]) {
      warnings.push(`Sensitive field '${field}' will be ignored during import.`);
    }
  });

  // Warn about secret field if present
  const server = configObj.server as Record<string, unknown>;
  if (server?.secret) {
    warnings.push('Server secret will be ignored during import for security reasons. Please configure it manually after import.');
  }

  // Note: provider field in knowledge bases will be automatically set to 'google'
  const knowledgeBases = configObj.knowledgeBases as Array<Record<string, unknown>>;
  if (knowledgeBases && Array.isArray(knowledgeBases) && knowledgeBases.length > 0) {
    // Only show info if there are knowledge bases (no need to warn - it's automatic)
    // Provider will be automatically set to 'google' during processing
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Process imported configuration and prepare for use
 */
export const processImportedConfig = (importedConfig: unknown): Partial<Tool> => {
  // Validate first
  const validation = validateImportedConfig(importedConfig);
  if (!validation.isValid) {
    throw new Error(`Invalid configuration: ${validation.errors.join(', ')}`);
  }

  // Remove sensitive fields - ensure it's an object first
  if (!importedConfig || typeof importedConfig !== 'object') {
    throw new Error('Invalid configuration format');
  }
  
  const cleanConfig = { ...importedConfig } as Record<string, unknown>;
  EXCLUDED_FIELDS.forEach(field => {
    delete cleanConfig[field];
  });

  // Remove secret from server config for security
  if (cleanConfig.server) {
    const server = cleanConfig.server as Record<string, unknown>;
    if (server.secret) {
      delete server.secret;
    }
  }

  // Add provider to knowledge bases (automatically managed by system - defaults to 'google')
  if (cleanConfig.knowledgeBases && Array.isArray(cleanConfig.knowledgeBases)) {
    cleanConfig.knowledgeBases = cleanConfig.knowledgeBases.map((kb: any) => {
      // Always ensure provider is set to 'google' (system default)
      return {
        ...kb,
        provider: 'google'
      };
    });
  }

  // Return processed config
  return {
    type: cleanConfig.type as Tool['type'],
    async: cleanConfig.async as boolean | undefined,
    function: cleanConfig.function as Tool['function'],
    server: cleanConfig.server as Tool['server'],
    messages: cleanConfig.messages as Tool['messages'],
    destinations: cleanConfig.destinations as Tool['destinations'],
    knowledgeBases: cleanConfig.knowledgeBases as Tool['knowledgeBases']
  };
};

/**
 * Read and parse JSON file
 */
export const readJsonFile = (file: File): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const json = JSON.parse(content);
        resolve(json);
      } catch {
        reject(new Error('Invalid JSON file format'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
};

